import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useHttpClient } from '@bonusly/http-client';

import { ApprovalRequest, Award } from '../../../../../../types/bonus';
import { ApiResponse, PaginatedParams, RequestParamsArg } from '../../../../types/api';
import { HttpError } from '../../../../types/error';
import { AwardType } from './enums';
import { ResourcePath } from './ResourcePath';
import { QueryKey } from './types';

export interface AwardApprovalRequestCreateArgs {
	body: {
		reason: string;
		giver_id: string;
		award_id: string;
	};
}

interface AwardsData {
	count: number;
	items: Award[];
}

type AwardsResponse = ApiResponse<AwardsData>;

interface AwardsDefaultParams {
	type?: AwardType;
	givable_by_user?: string;
}

type UseAwardsArgs = RequestParamsArg<AwardsIndexParams>;
type AwardsIndexParams = PaginatedParams<AwardsDefaultParams>;

export const useAwards = <T = AwardsResponse>(args: UseAwardsArgs = {}, options?: UseQueryOptions<AwardsResponse, HttpError, T>) => {
	const http = useHttpClient('v2');
	return useQuery<AwardsResponse, HttpError, T>([QueryKey.Awards, args], () => http.get(ResourcePath.Awards, { params: args.params }), options);
};

interface ApprovalRequestsData {
	count: number;
	items: ApprovalRequest[];
}

type ApprovalRequestsResponse = ApiResponse<ApprovalRequestsData>;

export const useApprovalRequests = <T = ApprovalRequestsResponse>(options?: UseQueryOptions<ApprovalRequestsResponse, HttpError, T>) => {
	const http = useHttpClient('v2');
	return useQuery<ApprovalRequestsResponse, HttpError, T>([QueryKey.ApprovalRequests], () => http.get(ResourcePath.ApprovalRequests), options);
};

export const usePendingAwards = () => {
	const { t } = useTranslation('recognition');
	const { data } = useApprovalRequests({
		select: (data) => data.data.result.items,
	});

	return {
		pendingText: data?.length ? t('awards.pending_banner.title', { count: data?.length }) : '',
		shouldDisplayPending: !!data?.length,
	};
};
